'use client'

import React from 'react'
import { isNil, isEmpty, pathOr, toLower } from 'ramda'
import { Card } from 'sidecar-components/atoms/Card'
import { Sentiment } from 'sidecar-components/types'

import {
  extractPreCheckedProvidersContent,
  checkProfessionalFacilityDuplicates,
  checkMultipleCategoryNames,
  checkForAnyNoSwipe,
} from './utils'
import { PreCheckedProviderModal } from './PreCheckedProviderModal'
import { Header } from './Header'
import { Description } from './Description'
import { Footer } from './Footer'
import { NoSwipe } from './NoSwipe'
import type { PreCheckedProvidersProps } from './PreCheckedProviders.types'

/**
 * PreCheckedProviders organism displays pre-checked provider information across
 * different contexts in the Sidecar Health platform.
 *
 * The component is comprised of multiple child components that are
 * conditionally rendered based on the usage context (poptart, care search,
 * EOC), provider type (professional, facility), insurance product, and
 * additional conditions such as professional and facility matches and having
 * multiple categories.
 *
 * The prechecked provider info comes from both Contentful and the backend.
 */
export const PreCheckedProviders: React.FC<PreCheckedProvidersProps> = ({
  className = '',
  content = {} as PreCheckedProvidersProps['content'],
  type = 'careDetail',
  preCheckedCategoryName = '',
  linkComponent,
  premember = false,
  hideHeading = false,
}) => {
  // Exit early if no content
  if (isNil(content) || isEmpty(content)) return null

  const { providers, sidecarCodeType } = content

  // Styles
  const containerStyles = 'mb-10 last:mb-0 inherit'
  const linkStyles =
    'color-textPrimary group-hover/row:color-textPrimary-hover group-active/row:color-textPrimary-active group-focus/row:color-textPrimary-hover sh-body'

  // ---------------------Prechecked Providers Logic---------------------
  const {
    preCheckedProvidersArr,
    isProfessional,
    isFacility,
    professionalProviders,
    facilityProviders,
  } = extractPreCheckedProvidersContent(content)

  // IF the provider and facility are the same, we combine this into
  // one row rather than showing them separately
  const hasMatchedProfessionalAndFacilityProviders =
    checkProfessionalFacilityDuplicates(
      professionalProviders,
      facilityProviders,
    )
  const hasMultipleCategoryNames = checkMultipleCategoryNames(providers)
  const hasAnyNoSwipe = checkForAnyNoSwipe(
    isProfessional ? professionalProviders : facilityProviders,
  )

  // exit early if on these pages if the preCheckedProvidersArr array is empty
  if (
    /^(careSearch|requestForm)$/.test(type) &&
    preCheckedProvidersArr.length === 0
  ) {
    return null
  }

  // preCheckedCategoryName will only be populated when a category is returned from contentful
  // if it is not populated, use the first provider's description
  const categoryName = !isEmpty(preCheckedCategoryName)
    ? preCheckedCategoryName
    : toLower(pathOr('', ['description'], professionalProviders[0])) ||
      toLower(pathOr('', ['description'], facilityProviders[0]))
  // if the category name has services in it, remove it
  const description = categoryName.replace(' services', '')

  // Don't render the tile if isFacility and there are no facility providers,
  // or if isProfessional and there are no professional providers
  if (
    (isFacility && facilityProviders.length === 0) ||
    (isProfessional && professionalProviders.length === 0)
  ) {
    return null
  }

  return (
    <>
      {type === 'requestForm' && (
        <>
          <Header
            description={description}
            isFacility={isFacility}
            isProfessional={isProfessional}
            professionalProviders={professionalProviders}
            providers={providers}
            sidecarCodeType={sidecarCodeType}
            type={type}
            preCheckedCategoryName={
              preCheckedCategoryName ? toLower(preCheckedCategoryName) : ''
            }
            hideHeading={hideHeading}
          />
          {preCheckedProvidersArr.map(provider => (
            <PreCheckedProviderModal
              borderOverflow={true}
              facilityProviders={facilityProviders}
              isProfessional={isProfessional}
              isFacility={isFacility}
              key={provider.providerUuid}
              linkStyles={linkStyles}
              provider={provider}
              professionalProviders={professionalProviders}
              type={type}
              hasMultipleCategoryNames={hasMultipleCategoryNames}
              preCheckedCategoryName={preCheckedCategoryName}
              showPreCheckTitle={false}
              linkComponent={linkComponent}
            />
          ))}
        </>
      )}

      {type !== 'requestForm' && (
        <>
          {/* Header Section */}
          {type !== 'episodeOfCare' && (
            <Header
              description={description}
              isFacility={isFacility}
              isProfessional={isProfessional}
              professionalProviders={professionalProviders}
              providers={providers}
              sidecarCodeType={sidecarCodeType}
              type={type}
              premember={premember}
              hideHeading={hideHeading}
            />
          )}

          <div className={containerStyles}>
            {/* Description Text for Care Details */}
            {(type === 'careDetail' || type === 'publicCareDetail') && (
              <Description
                description={description}
                facilityProviders={professionalProviders}
                isProfessional={isProfessional}
                professionalProviders={professionalProviders}
                type={type}
              />
            )}

            <Card className={className} sentiment={Sentiment.DEFAULT}>
              {/* Description text for Care Search */}
              <>
                {/^(careSearch)$/.test(type) && (
                  <Description
                    description={description}
                    facilityProviders={professionalProviders}
                    isProfessional={isProfessional}
                    professionalProviders={professionalProviders}
                    type={type}
                  />
                )}
                {preCheckedProvidersArr.map(provider => (
                  <PreCheckedProviderModal
                    borderOverflow={true}
                    facilityProviders={facilityProviders}
                    isProfessional={isProfessional}
                    isFacility={isFacility}
                    key={provider.providerUuid}
                    linkStyles={linkStyles}
                    provider={provider}
                    professionalProviders={professionalProviders}
                    type={type}
                    hasMultipleCategoryNames={hasMultipleCategoryNames}
                    preCheckedCategoryName={preCheckedCategoryName}
                    showPreCheckTitle={type === 'episodeOfCare'}
                    linkComponent={linkComponent}
                    premember={premember}
                  />
                ))}
              </>
            </Card>

            {hasAnyNoSwipe && (
              <NoSwipe linkComponent={linkComponent} premember={premember} />
            )}

            {/* This section is dynamic based on the providerType returned from the list for prechecked providers*/}
            {/* -------- Subtext section will be conditional --------  */}
            <Footer
              isFacility={isFacility}
              isProfessional={isProfessional}
              professionalProviders={professionalProviders}
              facilityProviders={facilityProviders}
              hasMatchedProfessionalAndFacilityProviders={
                hasMatchedProfessionalAndFacilityProviders
              }
              type={type}
              preCheckedCategoryName={preCheckedCategoryName}
              premember={premember}
            />
          </div>
        </>
      )}
    </>
  )
}

PreCheckedProviders.displayName = 'PreCheckedProviders'
